<template>
  <div class="public-api-token-search pa-10">

    <title-page icon="mdi-key">
      {{ $t('public-api-token.public-api-tokens') }}
    </title-page>

    <v-row v-if="currentUser.permissions.includes('OPERATOR_WRITE')">
      <v-col class="text-right">
        <v-btn small :to="{name: 'public-api-tokens.add'}">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('public-api-token.add') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mt-5">
      <v-col cols="12" md="4">
        <search-field
          v-model="filters.query"
          :placeholder="$t('public-api-token.search-placeholder')"
          clearable
          @input="onSearchChange"
        />
      </v-col>
    </v-row>

    <v-row no-gutters class="mt-5">
      <v-col>
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :options.sync="options"
          :server-items-length="total"
        >

          <template #item.tokenMasked="{item}">
            <code class="token-masked">{{ item.tokenMasked }}</code>
          </template>

          <template #item.enabled="{item}">
            <v-switch
              :input-value="item.enabled"
              dense
              hide-details
              class="mt-0"
              @change="toggleEnabled(item)"
            />
          </template>

          <template #item.description="{item}">
            <span v-if="item.description" class="text-truncate d-inline-block" style="max-width: 200px;">
              {{ item.description }}
            </span>
            <span v-else class="grey--text">-</span>
          </template>

          <template #item.createdAt="{item}">
            {{ item.createdAt | formatDate($t('format_datetime')) }}
          </template>

          <template #item.options="{item}">
            <v-row>
              <v-col>
                <v-btn small icon :to="{name: 'public-api-tokens.edit', params: {id: item.id}}">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn small icon @click="deleteToken(item.id)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import PublicApiTokenRepository from "@repository/PublicApiTokenRepository"

export default {
  data() {
    return {
      loading: false,
      items: [],
      total: 0,
      options: null,
      filters: {
        query: '',
      },
      searchTimeout: null,
    }
  },

  mounted() {
    if (!this.currentUser.isZE || !this.currentUser.permissions.includes('OPERATOR_WRITE')) {
      this.$router.replace({name: 'dashboard'})
      return
    }
  },

  watch: {
    options() {
      this.searchData()
    }
  },

  computed: {
    headers() {
      return [
        {text: this.$t('public-api-token.name'), value: 'name'},
        {text: this.$t('public-api-token.enabled'), value: 'enabled', sortable: false, width: 100},
        {text: this.$t('public-api-token.token'), value: 'tokenMasked', sortable: false},
        {text: '', value: 'options', width: 150, sortable: false},
      ]
    },
  },

  methods: {
    onSearchChange() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.searchData()
      }, 300)
    },

    searchData() {
      if (!this.options) return

      const options = this.options
      this.loading = true

      const offset = options.itemsPerPage * options.page - options.itemsPerPage
      const limit = options.itemsPerPage
      const orderBy = options.sortBy.length > 0 ? options.sortBy[0] : 'createdAt'
      const orderDirection = options.sortDesc.length > 0 && options.sortDesc[0] ? 'desc' : 'asc'

      PublicApiTokenRepository.search({
        query: this.filters.query,
        offset,
        limit,
        orderBy,
        orderDirection,
      }).then(result => {
        this.total = result.total
        this.items = result.items
        this.loading = false
      }).catch(err => {
        this.notifyError(err.message)
        this.loading = false
      })
    },

    deleteToken(tokenId) {
      this.$dialog.confirm({text: this.$t('public-api-token.confirm-delete')}).then(result => {
        if (result) {
          this.showLoading(true)
          PublicApiTokenRepository.delete(tokenId).then(() => {
            this.$dialog.notify.success(this.$t('delete-success'))
            this.showLoading(false)
            this.searchData()
          }).catch(err => {
            this.showLoading(false)
            this.notifyError(err)
          })
        }
      })
    },

    toggleEnabled(item) {
      const newEnabled = !item.enabled
      PublicApiTokenRepository.update(item.id, {enabled: newEnabled}).then(() => {
        item.enabled = newEnabled
        this.$dialog.notify.success(
          newEnabled
            ? this.$t('public-api-token.enabled-success')
            : this.$t('public-api-token.disabled-success')
        )
      }).catch(err => {
        this.notifyError(err)
      })
    },
  }
}
</script>

<style lang="scss">
.public-api-token-search {
  .token-masked {
    font-size: 12px;
    background-color: #f5f5f5;
    padding: 4px 8px;
    border-radius: 4px;
  }
}
</style>
